import PropTypes from 'prop-types';
import React from 'react';
import _findIndex from 'lodash/findIndex';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import Col from 'ravenjs/lib/Col';
import Link from 'ravenjs/lib/Link';
import Row from 'ravenjs/lib/Row';
import { getThemeProps } from 'ravenjs/utils/theme';

import Box from 'ravenjs/lib/Box';
import Typography from 'ravenjs/lib/Typography';
import { InfoIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import DescriptionField from './DescriptionField';
import FormGroupContent from './FormGroupContent';
import TitleField from './TitleField';

const HandbookAlertBanner = styled(Box)`
    display: flex;
    background-color: ${DEFAULT_COLORS.LIGHTBLUE1};
    border-color: ${DEFAULT_COLORS.LIGHTBLUE};
    color: ${DEFAULT_COLORS.DARK};
`;

const ObjectFieldTemplateStyled = styled.div`
    /**
     * Add all of the remaining styles from theme
     */
    ${getThemeProps('ObjectFieldTemplate.styles')};
`;

function ObjectFieldTemplate(props) {
    const { description, formContext, idSchema, properties, required, schema, formData } = props;

    const formGroup = _get(schema, 'formGroup', false);
    const titleInsideFormGroup = _get(schema, 'titleInsideFormGroup', false);
    const formGroupTitle = _get(schema, 'formGroupTitle');
    const platformLoginURL = _get(formData, 'platformLoginURL', '');
    const uiSchemaTitle = _get(props, 'uiSchema.ui:title');
    const propsTitle = _get(props, 'title');
    const schemaId = _get(idSchema, '$id');
    const titleId = `${schemaId}__title`;
    const descriptionId = `${schemaId}__description`;
    const uiOptions = _get(props, 'uiSchema.ui:options', {});
    const rowProps = _get(props, 'uiSchema.ui:options.row', {});
    const isRoot = schemaId === 'root';
    const gutter = typeof rowProps.gutter === 'undefined' ? false : rowProps.gutter;
    const displayTitle = _get(props, 'uiSchema.ui:options.label', true);
    const bannerEnabled = _get(schema, 'bannerEnabled', false);
    const bannerText = _get(schema, 'bannerText', false);
    const hasCustomRequiredValidation = _get(schema, 'hasCustomRequiredValidation', false);
    const showRequired = hasCustomRequiredValidation && _isEmpty(platformLoginURL);
    const addIdAttribute = _get(uiOptions, 'addIdAttribute', false);
    const actionLink = _get(uiOptions, 'actionLink', null);
    const effectiveTitle =
        propsTitle || uiSchemaTitle || (titleInsideFormGroup ? formGroupTitle : undefined);

    const renderTitle = () =>
        displayTitle && (
            <TitleField
                formContext={formContext}
                id={titleId}
                required={required}
                root={isRoot}
                title={effectiveTitle}
                showRequired={showRequired}
                uiOptions={titleInsideFormGroup ? {} : uiOptions}
            />
        );

    const renderActionLink = () => {
        if (!titleInsideFormGroup || !actionLink?.show) return null;
        const useMouseDown = _get(actionLink, 'useMouseDown', false);
        const formDescriptionText = _get(actionLink, 'formDescriptionText', null);
        const handleClick = e => {
            e.preventDefault();
            if (actionLink.onClick) {
                actionLink.onClick(e);
            } else if (actionLink.scrollToId) {
                const el = document.getElementById(actionLink.scrollToId);
                if (el) {
                    const offset = actionLink.scrollOffset || 100;
                    const elementPosition = el.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
            }
        };
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: formDescriptionText ? 'space-between' : 'flex-end',
                    alignItems: 'center',
                    marginBottom: '4px',
                }}
            >
                {formDescriptionText && (
                    <Typography
                        color="#848484"
                        fontSize="0.875rem"
                        fontFamily="Roboto"
                        fontWeight="400"
                        lineHeight="19px"
                        gutterLeft="0.938rem"
                        gutterBottom="0"
                        gutterTop="0"
                    >
                        {formDescriptionText}
                    </Typography>
                )}
                <Link
                    onClick={useMouseDown ? undefined : handleClick}
                    onMouseDown={useMouseDown ? handleClick : undefined}
                    style={{
                        fontSize: actionLink.fontSize || '14px',
                        cursor: 'pointer',
                        marginRight: '15px',
                        lineHeight: '1.2',
                        fontFamily: actionLink.fontFamily || 'Favorit',
                        ...actionLink.style,
                    }}
                >
                    {actionLink.label || 'Link'}
                </Link>
            </div>
        );
    };

    const renderBanner = () =>
        bannerEnabled ? (
            <HandbookAlertBanner
                borderRadius="0.25rem"
                boxShadow="none"
                margin="0 0 1rem 0"
                padding="0.875rem"
            >
                <InfoIcon
                    fill={DEFAULT_COLORS.CATEGORY_DISCRIMINATION_EQUAL_EMPLOYMENT}
                    width="24"
                    height="24"
                    margin="0 0.5rem 0 0"
                />
                <Typography
                    fontSize="0.875rem"
                    gutterBottom="0 !important"
                    color={DEFAULT_COLORS.BLACK}
                    fontWeight="bold"
                    type="pBody"
                >
                    {bannerText}
                </Typography>
            </HandbookAlertBanner>
        ) : null;

    const renderProperty = prop => {
        // Extract the `col` props, if available.
        const colProps = _get(prop, 'content.props.uiSchema.ui:options.col', {});
        // Extract the gutter from `colProps`.
        const colGutterProp = _get(colProps, 'gutter');
        // Define the gutter for the column.
        const colGutter = typeof colGutterProp === 'undefined' ? true : colGutterProp;
        // Extract the content to render.
        const content = _get(prop, 'content');
        // To fix the margins, we need to match the last index with current index.
        const currentIndex = _findIndex(properties, prop);
        // Should we remove the bottom margin?
        const removeBottomMargin = currentIndex === properties.length - 1;
        // Return the Column
        return (
            <Col
                key={prop.name}
                gutter={isRoot ? false : colGutter}
                {...colProps}
                margin={removeBottomMargin ? '0 0 -15px 0' : colProps.margin || ''}
                size={colProps.size || 12}
            >
                {content}
            </Col>
        );
    };
    const renderRow = items => {
        return (
            <Row justify="space-between" {...rowProps} gutter={gutter}>
                {items.map(prop => renderProperty(prop))}
            </Row>
        );
    };
    const renderContent = () =>
        formGroup ? (
            <FormGroupContent
                schema={schema}
                title={titleInsideFormGroup ? effectiveTitle : undefined}
            >
                {titleInsideFormGroup && addIdAttribute && <span id={titleId} />}
                {titleInsideFormGroup && !_isEmpty(description) && (
                    <DescriptionField
                        description={description}
                        formContext={formContext}
                        id={descriptionId}
                        root={isRoot}
                        gutterLeft="18px"
                        gutterTop="0"
                        gutterBottom="10px"
                    />
                )}
                {renderRow(properties)}
            </FormGroupContent>
        ) : (
            renderRow(properties)
        );
    const renderDescription = () =>
        !_isEmpty(description) && (
            <DescriptionField
                description={description}
                formContext={formContext}
                id={descriptionId}
                root={isRoot}
            />
        );

    const shouldHideTitle = titleInsideFormGroup;

    return (
        <ObjectFieldTemplateStyled>
            {!shouldHideTitle && renderTitle()}
            {isRoot && renderDescription()}
            {renderBanner()}
            {renderActionLink()}
            {renderContent()}
        </ObjectFieldTemplateStyled>
    );
}

ObjectFieldTemplate.propTypes = {
    description: PropTypes.string,
    formContext: PropTypes.object,
    idSchema: PropTypes.object,
    properties: PropTypes.array,
    required: PropTypes.bool,
    schema: PropTypes.object.isRequired,
    formData: PropTypes.object,
};

ObjectFieldTemplate.defaultProps = {
    description: '',
    formContext: {},
    idSchema: {},
    properties: [],
    required: false,
    formData: {},
};

export default ObjectFieldTemplate;
